/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { getCookie } from '../../../hookServices/cookiesManager';
import { getClientId } from '../../../hookServices/clientId';
import { HOST, PORT } from '../../../environment';
import Spinner from '../../loaders/globalSpinner/Spinner';
import { resolveStartManyPorts } from '../../../pages/startPage/openStartManyForStation';

async function fetchStationByNumber(portNumber) {
  const position = getCookie('userLocation');
  const clientId = getClientId();
  const response = await fetch(
    `${HOST}:${PORT}/api/device/v2/station?station_number=${portNumber}&lat=${position?.latitude || ''}&lon=${position?.longitude || ''}&clientUiId=${clientId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error('Response is not JSON');
  }

  return response.json();
}

/** Expand startMany ?ports= to all connectors that share each station's deviceId. */
async function expandPortsByDeviceId(portNumbers) {
  const requested = (portNumbers || []).map(p => String(p).trim()).filter(Boolean);
  if (requested.length === 0) {
    return [];
  }

  const expanded = new Set(requested);
  const seenDeviceIds = new Set();

  await Promise.all(
    requested.map(async portNumber => {
      try {
        const station = await fetchStationByNumber(portNumber);
        const deviceId = station?.deviceId != null ? String(station.deviceId).trim() : '';
        if (!deviceId || seenDeviceIds.has(deviceId)) {
          return;
        }
        seenDeviceIds.add(deviceId);
        const siblings = await resolveStartManyPorts(station, portNumber);
        siblings.forEach(num => {
          const n = String(num).trim();
          if (n) {
            expanded.add(n);
          }
        });
      } catch (error) {
        console.error(`Error expanding ports for ${portNumber}:`, error);
      }
    })
  );

  return [...expanded];
}

export default function StartManyLayout({ fingerPrint }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const portsParam = searchParams.get('ports');
  const portNumbersFromUrl = portsParam
    ? portsParam
        .split(',')
        .map(p => p.trim())
        .filter(Boolean)
    : [];

  const [stations, setStations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchStations = async () => {
      if (portNumbersFromUrl.length === 0) {
        setIsError(true);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setIsError(false);

        const portNumbers = await expandPortsByDeviceId(portNumbersFromUrl);
        if (portNumbers.length === 0) {
          setIsError(true);
          setIsLoading(false);
          return;
        }

        // Keep shareable URL in sync when a single port expands to the full device.
        const urlPorts = portNumbersFromUrl.slice().sort().join(',');
        const expandedPorts = portNumbers.slice().sort().join(',');
        if (expandedPorts !== urlPorts) {
          setSearchParams({ ports: portNumbers.join(',') }, { replace: true });
        }

        const stationPromises = portNumbers.map(async portNumber => {
          try {
            const stationData = await fetchStationByNumber(portNumber);
            return {
              ...stationData,
              portNumber,
              isLoading: false,
              isError: false,
            };
          } catch (error) {
            console.error(`Error fetching station ${portNumber}:`, error);
            return {
              portNumber,
              isLoading: false,
              isError: true,
            };
          }
        });

        const results = await Promise.all(stationPromises);
        setStations(results);
        setIsLoading(false);
        setIsError(results.some(result => result.isError));
      } catch (error) {
        console.error('Error fetching stations:', error);
        setIsError(true);
        setIsLoading(false);
      }
    };

    fetchStations();
  }, [portNumbersFromUrl.join(','), fingerPrint]); // Use stable dependency

  if (isLoading) {
    return <Spinner pageCentered />;
  }

  return (
    <Outlet
      context={{
        stations,
        portNumbers: stations.map(s => s.portNumber).filter(Boolean),
        isFetchingStations: false,
        isLoadingStations: false,
        isErrorStations: isError,
      }}
    />
  );
}
